import { buildOutcomeOverrideRows } from 'features/libra/stepComponents/niiReport/content/buildNiiReportContent'
import {
  buildNiiReviewMilestoneTable,
  buildNiiSignoffTable,
  NII_APPROVED_BEFORE_SIGNOFF_NOTE,
} from 'features/libra/stepComponents/niiReport/content/buildNiiReviewContent'
import { buildZoneOverridesTable } from 'features/libra/stepComponents/niiReport/content/buildNiiZoneAnalysisContent'
import type { NiiReportSection } from 'features/libra/stepComponents/niiReport/content/niiReportContentTypes'
import type { PdfTableData } from 'features/reporting/pdfGeneration/components/shared/PdfTable'

/**
 * Section cards are rendered all-or-nothing (`wrap={false}`) whenever they can
 * fit on a single page, so a card never straddles a page break. react-pdf
 * TRUNCATES unbreakable content taller than one page, so the decision must be
 * conservative: this module deliberately over-estimates heights, and only
 * sections confidently under the one-page budget are kept atomic. Everything
 * else falls back to block-level wrapping (splitting between rows/blocks).
 *
 * Budget derivation (LETTER, 612×792pt): 792 − page padding (16 top + 40
 * bottom) − fixed header (40 + 18 margin) − content margins (10 + 40) −
 * footer clearance (60) ≈ 568pt of card space per page.
 */
export const NII_PDF_SINGLE_PAGE_BUDGET = 560

const SAFETY_FACTOR = 1.15

// Layout estimates mirror PdfStyles, NiiPdfStyles and ReportPage. These are
// estimates for the fit decision only — being generous is safe, being tight
// risks truncation.
const CARD_BODY_WIDTH = 530
const LABEL_TEXT_WIDTH = 139
const VALUE_TEXT_WIDTH = 351
const BODY_FONT = 10
const TABLE_FONT = 9
const LINE_HEIGHT = 1.5
/** Average Helvetica glyph width as a fraction of font size (generous). */
const CHAR_WIDTH_FACTOR = 0.55

const CARD_CHROME = 55 // title bar + body padding + borders
const SUBSECTION_TITLE = 30
const BLOCK_GAP = 10
const ZONE_BLOCK_CHROME = 60 // padding + heading + bottom margin
const IMAGE_MAX = 430 // zoneImage maxHeight + label + margin
const EMPTY_STATE = 18

function textHeight(text: string, fontSize: number, width: number): number {
  const charsPerLine = Math.max(
    1,
    Math.floor(width / (fontSize * CHAR_WIDTH_FACTOR))
  )
  const lines = text
    .split('\n')
    .reduce(
      (total, line) =>
        total + Math.max(1, Math.ceil(line.length / charsPerLine)),
      0
    )
  return lines * fontSize * LINE_HEIGHT
}

function descriptionRowHeight(label: string, value: string): number {
  const labelHeight = textHeight(label, BODY_FONT, LABEL_TEXT_WIDTH) + 16
  const valueHeight = textHeight(value, BODY_FONT, VALUE_TEXT_WIDTH) + 16
  return Math.max(28, labelHeight, valueHeight)
}

function tableHeight(table: PdfTableData): number {
  const widths =
    table.columnWidths ??
    table.headers.map(() => 1 / Math.max(table.headers.length, 1))
  const rowHeight = (cells: ReadonlyArray<string>): number =>
    Math.max(
      20,
      ...cells.map(
        (cell, i) =>
          textHeight(
            cell,
            TABLE_FONT,
            (widths[i] ?? 0.3) * CARD_BODY_WIDTH - 10
          ) + 10
      )
    )
  const header = rowHeight(table.headers)
  return header + table.rows.reduce((sum, row) => sum + rowHeight(row.cells), 0)
}

function titledTableHeight(table: PdfTableData | undefined): number {
  return table ? SUBSECTION_TITLE + tableHeight(table) + BLOCK_GAP : 0
}

/**
 * Conservative estimate of a step section card's rendered height in points.
 */
export function estimateNiiStepSectionHeight(step: NiiReportSection): number {
  let height = CARD_CHROME

  for (const list of step.questionLists ?? []) {
    height += list.title ? SUBSECTION_TITLE : 0
    for (const { label, value } of list.override
      ? buildOutcomeOverrideRows(list.override)
      : []) {
      height += descriptionRowHeight(label, value)
    }
    for (const { question } of list.answers) {
      height += Math.max(
        26,
        textHeight(question, BODY_FONT, CARD_BODY_WIDTH - 50) + 16
      )
    }
    height += BLOCK_GAP
  }

  for (const block of step.formBlocks ?? []) {
    height += SUBSECTION_TITLE
    for (const row of block.rows) {
      height += descriptionRowHeight(row.label, row.value)
    }
    height += BLOCK_GAP
  }

  for (const block of step.arrayTables ?? []) {
    height += SUBSECTION_TITLE
    if (block.entries.length === 0) {
      height += EMPTY_STATE
    }
    for (const entry of block.entries) {
      for (const cell of entry.cells) {
        height += descriptionRowHeight(cell.label, cell.value)
      }
      height += 16 // divider
    }
    height += BLOCK_GAP
  }

  if (step.zoneInventory) {
    if (step.zoneInventory.length === 0) height += EMPTY_STATE
    for (const zone of step.zoneInventory) {
      height += ZONE_BLOCK_CHROME
      height += zone.description
        ? descriptionRowHeight('Description', zone.description)
        : 0
      height += titledTableHeight(zone.designAndFunction)
      height += titledTableHeight(zone.damageMechanisms)
    }
  }

  height += (step.zoneImages?.length ?? 0) * IMAGE_MAX
  if (step.zoneImages?.length) height += SUBSECTION_TITLE

  if (step.zoneAnalysis) {
    const analysis = step.zoneAnalysis
    height += analysis.zoneBreakdownJustification
      ? SUBSECTION_TITLE +
        textHeight(
          analysis.zoneBreakdownJustification,
          BODY_FONT,
          CARD_BODY_WIDTH
        )
      : 0
    height += analysis.overview
      ? SUBSECTION_TITLE + tableHeight(analysis.overview)
      : 0
    for (const zone of analysis.zones) {
      height += ZONE_BLOCK_CHROME
      height += titledTableHeight(zone.damageMechanisms)
      height += titledTableHeight(zone.designAndFunction)
      if (zone.strategy) {
        height += titledTableHeight(zone.strategy.table)
        height += zone.strategy.overallDecision ? 30 : 0
      }
      height += titledTableHeight(zone.rba)
      height += titledTableHeight(zone.coverageRequirements)
      if (zone.coverageBreakdown) {
        height += SUBSECTION_TITLE + 60 // percent + subtext
        height += zone.coverageBreakdown.formulaLines.length * 16 + 16
        height += zone.coverageBreakdown.modifiers.length > 0 ? 24 : 0
      }
      if (zone.overrides) {
        height += 24 // divider
        height += titledTableHeight(buildZoneOverridesTable(zone.overrides))
      }
    }
  }

  if (step.reportCloseout) {
    const closeout = step.reportCloseout
    height += closeout.executiveSummary
      ? SUBSECTION_TITLE +
        textHeight(closeout.executiveSummary, BODY_FONT, CARD_BODY_WIDTH)
      : 0
    height += closeout.internalComments
      ? SUBSECTION_TITLE +
        textHeight(closeout.internalComments, BODY_FONT, CARD_BODY_WIDTH)
      : 0
    height += SUBSECTION_TITLE // Conclusion
    height += 34 // eligibility tag row
    if (closeout.recommendations.length > 0) {
      const itemsHeight = closeout.recommendations.reduce(
        (sum, item) =>
          sum + textHeight(item, BODY_FONT, VALUE_TEXT_WIDTH - 20) + 4,
        0
      )
      height += Math.max(28, itemsHeight + 16)
    }
    height += closeout.screenedOutAtPreScreening
      ? descriptionRowHeight('Pre-screening', 'Screened out at pre-screening')
      : 0
    height += closeout.justification
      ? descriptionRowHeight('Justification', closeout.justification)
      : 0
    if (closeout.zoneRecommendations.length > 0) {
      height += SUBSECTION_TITLE
      for (const zone of closeout.zoneRecommendations) {
        height += ZONE_BLOCK_CHROME
        height += zone.zone_recommendations.reduce(
          (sum, item) =>
            sum + textHeight(item, BODY_FONT, CARD_BODY_WIDTH - 30) + 4,
          0
        )
      }
    }
  }

  if (step.reviewMilestones) {
    height += tableHeight(buildNiiReviewMilestoneTable(step.reviewMilestones))
    height += step.signoff ? BLOCK_GAP : 0 // divider and the gap below it
  }
  if (step.signoff) height += tableHeight(buildNiiSignoffTable(step.signoff))
  if (step.approvedBeforeSignoffRequired) {
    height +=
      textHeight(NII_APPROVED_BEFORE_SIGNOFF_NOTE, BODY_FONT, CARD_BODY_WIDTH) +
      BLOCK_GAP
  }

  const isEmpty =
    !step.questionLists?.length &&
    !step.formBlocks?.length &&
    !step.arrayTables?.length &&
    step.zoneInventory === undefined &&
    !step.zoneAnalysis &&
    !step.reportCloseout &&
    !step.signoff &&
    !step.reviewMilestones &&
    !step.zoneImages?.length
  if (isEmpty) height += EMPTY_STATE

  return height
}

/**
 * True when the section is confidently small enough to render all-or-nothing
 * on a single page. Larger sections must stay wrappable — an unbreakable
 * element taller than one page would be truncated by react-pdf.
 */
export function niiSectionFitsOnOnePage(step: NiiReportSection): boolean {
  return (
    estimateNiiStepSectionHeight(step) * SAFETY_FACTOR <=
    NII_PDF_SINGLE_PAGE_BUDGET
  )
}
